/**
 * Shared click handling for the copy-to-clipboard button that renderMarkdown
 * emits beside every fenced code block (`div.code-block-wrapper >
 * button.copy-code-btn`). Both the chat message thread and the work-item
 * activity thread render that markup via `innerHTML` and delegate clicks
 * from a container, so the lookup/copy/feedback logic lives here once.
 */

const COPIED_CLASS = "copied";
const COPIED_DURATION_MS = 2000;

/**
 * Given the target of a click event, find an enclosing `.copy-code-btn`,
 * copy the text of the `pre` in its `.code-block-wrapper` to the clipboard,
 * and toggle the button's `copied` class for `COPIED_DURATION_MS`.
 *
 * Returns synchronously whether the click was handled (i.e. it originated
 * on a copy button), so callers can call `preventDefault` / `stopPropagation`
 * during the event's dispatch phase — those calls are no-ops once an `await`
 * has yielded to the microtask queue. The clipboard write itself is async
 * and fire-and-forget; failures are swallowed silently.
 */
export function handleCopyCodeBlockClick(target: EventTarget | null): boolean {
  const btn = (target as HTMLElement | null)?.closest?.(
    ".copy-code-btn",
  ) as HTMLElement | null;
  if (!btn) return false;

  const wrapper = btn.closest(".code-block-wrapper");
  const pre = wrapper?.querySelector("pre");
  if (pre) {
    void (async () => {
      try {
        await navigator.clipboard.writeText(pre.textContent ?? "");
        btn.classList.add(COPIED_CLASS);
        setTimeout(
          () => btn.classList.remove(COPIED_CLASS),
          COPIED_DURATION_MS,
        );
      } catch {
        // Clipboard API not available — silent fail
      }
    })();
  }
  return true;
}
